/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Square shell — Reglas del POS.
 * Solo reglas de negocio: cobro fiscal, stock, topes, PIN.
 * El hardware (impresora, balanza, lector) vive en Dispositivos.
 */
import React from 'react';
import { Text, TextInput, View } from 'react-native';
import { settingsStyles as styles } from './settingsScreen.styles';
import { useContentLayout } from '../../shared/layout/useContentLayout';
import { ticketColors } from '../../shared/theme/ticketColors';
import { spacing } from '../../shared/theme/spacing';
import { STOCK_SALE_POLICY_OPTIONS } from '../../reports/public-api';
import {
  OperationalFilterChipGroup,
  OperationalFilterFieldLabel,
  OperationalFilterSegmentedRow,
} from '../../shared/filters/OperationalFilterPrimitives';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { BranchSettingsHubShell } from './BranchSettingsHubShell';
import { PaperSheet } from '../../shared/ticket/PaperSheet';
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader';
import type { PosFiscalDefaultStrategy } from '../../fiscal/domain-api';
import { SettingsSaveCta, hubStyles } from './components/settingsHubUi';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';

/** Hub: cobro fiscal, venta sin stock, items repetidos, topes y PIN supervisor. */
export function BranchPosOpsScreen() {
  const { layoutWide } = useContentLayout('settings');
  const hasKitchenTickets = useHasBusinessCapability('kitchen_tickets');
  const inventoryStockUxEnabled = useHasBusinessCapability('inventory');
  const productCombosEnabled = useHasBusinessCapability('product_combos');

  return (
    <BranchSettingsHubShell>
      {(vm) => {
        const branchName = vm.currentBranch?.name ?? '';
        const topesDirty =
          Number(vm.manualDiscountPercentInput.replace(',', '.')) !==
            (vm.currentBranch?.manualDiscountMaxPercent ?? 20) ||
          Number(vm.manualDiscountAmountInput.replace(',', '.')) !==
            (vm.currentBranch?.manualDiscountMaxAmount ?? 5000);

        return (
          <View style={hubStyles.stack}>
            <TicketModuleHeader
              kicker="Ajustes"
              title="Reglas del POS"
              meta={branchName || undefined}
            />

            {vm.posFiscalSettingsReady ? (
              <PaperSheet padded>
                <Text style={styles.cardTitle}>Cobro en POS</Text>
                <Text style={styles.captionBlock}>
                  Comprobante por defecto en todas las sucursales. En cada venta podés cambiarlo.
                </Text>
                {!vm.fiscalOptionInPlan ? (
                  <Text style={hubStyles.hintSecondary}>Tu plan no incluye facturación fiscal.</Text>
                ) : null}
                {vm.isOwner ? (
                  <View style={hubStyles.controlBlock}>
                    <OperationalFilterSegmentedRow
                      ticketTone
                      layout="hug"
                      options={[
                        ...(vm.fiscalOptionInPlan
                          ? [
                              {
                                value: 'fiscal' as PosFiscalDefaultStrategy,
                                label: 'Fiscal',
                                accessibilityLabel: 'Fiscal',
                              },
                              {
                                value: 'auto_by_payment' as PosFiscalDefaultStrategy,
                                label: 'Según el cobro',
                                accessibilityLabel: 'Según el cobro',
                              },
                            ]
                          : []),
                        {
                          value: 'non_fiscal' as PosFiscalDefaultStrategy,
                          label: 'No fiscal',
                          accessibilityLabel: 'No fiscal',
                        },
                      ]}
                      value={vm.defaultPosFiscalMode}
                      onChange={(mode) => {
                        if (vm.savingDefaultPosFiscal) return;
                        void vm.handlePersistDefaultPosFiscalMode(mode);
                      }}
                    />
                    {vm.defaultPosFiscalMode === 'auto_by_payment' ? (
                      <Text style={hubStyles.hintSecondary}>
                        Transferencia o QR de Mercado Pago salen con factura. Efectivo y fiado, con ticket
                        interno.
                      </Text>
                    ) : null}
                  </View>
                ) : (
                  <Text style={hubStyles.hintSecondary}>
                    {vm.defaultPosFiscalMode === 'fiscal'
                      ? 'Fiscal'
                      : vm.defaultPosFiscalMode === 'auto_by_payment'
                        ? 'Según el cobro'
                        : 'No fiscal'}{' '}
                    · lo define el dueño
                  </Text>
                )}
              </PaperSheet>
            ) : null}

            {inventoryStockUxEnabled ? (
              <PaperSheet padded>
                <Text style={styles.cardTitle}>Venta sin stock</Text>
                <Text style={styles.captionBlock}>
                  Bloquear frena la venta. Advertir avisa pero deja cobrar. Permitir ni avisa.
                </Text>
                <View style={hubStyles.controlBlock}>
                  <OperationalFilterChipGroup
                    ticketTone
                    layout="hug"
                    options={STOCK_SALE_POLICY_OPTIONS.map((opt) => ({
                      value: opt.id,
                      label: opt.label,
                      accessibilityLabel: `Política: ${opt.label}`,
                    }))}
                    value={vm.stockSalePolicy}
                    disabled={!vm.canEditPosRules}
                    onChange={(id) => {
                      void vm.handleSaveStockPolicy(id);
                    }}
                  />
                </View>
              </PaperSheet>
            ) : null}

            {inventoryStockUxEnabled && productCombosEnabled ? (
              <PaperSheet padded testID="branch.sellable-modifier.card">
                <Text style={styles.cardTitle}>Vendible en combos</Text>
                <Text style={styles.captionBlock}>
                  Solo carta compuesta. Base mira la composición. Peor caso resta como si eligieran la opción que más
                  insumo gasta (ej. triple).
                </Text>
                <View style={hubStyles.controlBlock}>
                  <OperationalFilterSegmentedRow
                    ticketTone
                    layout="hug"
                    options={[
                      { value: 'base', label: 'Composición', accessibilityLabel: 'Vendible por composición' },
                      {
                        value: 'worst_case',
                        label: 'Peor opción',
                        accessibilityLabel: 'Vendible asumiendo peor opción de modifiers',
                      },
                    ]}
                    value={vm.sellableModifierAssumption}
                    onChange={(v) => {
                      if (!vm.canEditPosRules || vm.branchPolicySaving) return;
                      void vm.handleSaveSellableModifierAssumption(v === 'worst_case' ? 'worst_case' : 'base');
                    }}
                  />
                </View>
              </PaperSheet>
            ) : null}

            <PaperSheet padded testID="branch.consolidate.card">
              <Text style={styles.cardTitle}>Items repetidos</Text>
              <Text style={styles.captionBlock}>
                Si escaneás dos veces lo mismo, Juntar lo suma en una línea. No junta si cambia variante, nota o
                precio.
              </Text>
              <View style={hubStyles.controlBlock}>
                <OperationalFilterSegmentedRow
                  ticketTone
                  layout="hug"
                  options={[
                    { value: 'on', label: 'Juntar', accessibilityLabel: 'Juntar items repetidos' },
                    { value: 'off', label: 'Separar', accessibilityLabel: 'Separar items repetidos' },
                  ]}
                  value={vm.consolidateCartLines ? 'on' : 'off'}
                  onChange={(v) => {
                    if (!vm.canEditPosRules || vm.branchPolicySaving) return;
                    void vm.handleSaveConsolidateCartLines(v === 'on');
                  }}
                />
              </View>
            </PaperSheet>

            {hasKitchenTickets ? (
              <PaperSheet padded testID="branch.kitchen-fire-mode.card">
                <Text style={styles.cardTitle}>Cuándo sale la comanda</Text>
                <Text style={styles.captionBlock}>
                  Después de cobrar es lo habitual. Antes de cobrar deja mandar la comanda a cocina mientras el
                  cliente paga; la entrega queda bloqueada hasta que el cobro se confirme.
                </Text>
                <View style={hubStyles.controlBlock}>
                  <OperationalFilterSegmentedRow
                    ticketTone
                    layout="hug"
                    options={[
                      { value: 'after_payment', label: 'Después de cobrar', accessibilityLabel: 'Comanda después de cobrar' },
                      { value: 'before_payment', label: 'Antes de cobrar', accessibilityLabel: 'Comanda antes de cobrar' },
                    ]}
                    value={vm.kitchenFireMode}
                    onChange={(v) => {
                      if (!vm.canEditPosRules || vm.branchPolicySaving) return;
                      void vm.handleSaveKitchenFireMode(v === 'before_payment' ? 'before_payment' : 'after_payment');
                    }}
                  />
                </View>
                <View style={[hubStyles.controlBlock, { marginTop: spacing.sm }]}>
                  <OperationalFilterFieldLabel>Al marcar entregado</OperationalFilterFieldLabel>
                  <Text style={styles.captionBlock}>
                    Si el pedido online pasa a entregado, podemos sacar la comanda de Listo solas.
                  </Text>
                  <OperationalFilterSegmentedRow
                    ticketTone
                    layout="hug"
                    options={[
                      { value: 'on', label: 'Archivar comanda', accessibilityLabel: 'Archivar comanda al entregar' },
                      { value: 'off', label: 'Dejar en listo', accessibilityLabel: 'Dejar comanda en listo al entregar' },
                    ]}
                    value={vm.kitchenAutoArchiveOnDelivered ? 'on' : 'off'}
                    onChange={(v) => {
                      if (!vm.canEditPosRules || vm.branchPolicySaving) return;
                      void vm.handleSaveKitchenAutoArchiveOnDelivered(v === 'on');
                    }}
                  />
                </View>
              </PaperSheet>
            ) : null}

            {vm.promotionsIntegrationsEnabled ? (
              <PaperSheet padded>
                <Text style={styles.cardTitle}>Topes manuales</Text>
                <Text style={styles.captionBlock}>
                  Hasta cuánto puede descontar un empleado. A vos no te limita.
                </Text>
                <View style={[hubStyles.controlBlock, layoutWide && hubStyles.inputBlockWide]}>
                  <View style={layoutWide ? hubStyles.inputsRowWide : hubStyles.inputsStack}>
                    <View style={layoutWide ? hubStyles.inputCol : undefined}>
                      <OperationalFilterFieldLabel>Tope (%)</OperationalFilterFieldLabel>
                      <NumericTextInput
                        kind="percent"
                        style={hubStyles.inputTicket}
                        value={vm.manualDiscountPercentInput}
                        onChangeText={vm.setManualDiscountPercentInput}
                        placeholder="Ej. 20"
                        placeholderTextColor={ticketColors.ink3}
                        editable={vm.canEditPosRules && !vm.manualDiscountSaving}
                      />
                    </View>
                    <View style={layoutWide ? hubStyles.inputCol : undefined}>
                      <OperationalFilterFieldLabel>Tope ($)</OperationalFilterFieldLabel>
                      <NumericTextInput
                        kind="decimalMoney"
                        style={hubStyles.inputTicket}
                        value={vm.manualDiscountAmountInput}
                        onChangeText={vm.setManualDiscountAmountInput}
                        placeholder="Ej. 5000"
                        placeholderTextColor={ticketColors.ink3}
                        editable={vm.canEditPosRules && !vm.manualDiscountSaving}
                      />
                    </View>
                  </View>
                  {vm.canEditPosRules ? (
                    <SettingsSaveCta
                      label={topesDirty ? 'Guardar cambios' : 'Guardar'}
                      saving={vm.manualDiscountSaving}
                      layoutWide={layoutWide}
                      onPress={() => void vm.handleSaveManualDiscountThresholds()}
                      disabled={vm.manualDiscountSaving || !topesDirty}
                      testID="branch.topes.save"
                    />
                  ) : null}
                </View>
              </PaperSheet>
            ) : null}

            <PaperSheet padded>
              <Text style={styles.cardTitle}>PIN de esta sucursal</Text>
              <Text style={styles.captionBlock}>
                Los empleados lo usan para anular, descontar o cerrar caja con diferencia.
              </Text>
              <Text style={hubStyles.hintSecondary}>
                {vm.supervisorPinConfigured ? 'Hay un PIN cargado.' : 'Todavía no hay PIN.'}
              </Text>
              {vm.canEditPosRules ? (
                <View style={[hubStyles.controlBlock, layoutWide && hubStyles.inputBlockWide]}>
                  <OperationalFilterFieldLabel>Nuevo PIN</OperationalFilterFieldLabel>
                  <TextInput
                    style={hubStyles.inputTicket}
                    value={vm.supervisorPinInput}
                    onChangeText={(t) => vm.setSupervisorPinInput(t.replace(/[^\d]/g, '').slice(0, 12))}
                    placeholder="Nuevo PIN"
                    placeholderTextColor={ticketColors.ink3}
                    keyboardType="number-pad"
                    secureTextEntry
                    maxLength={12}
                    editable={!vm.supervisorPinSaving}
                  />
                  <SettingsSaveCta
                    label="Guardar PIN"
                    saving={vm.supervisorPinSaving}
                    layoutWide={layoutWide}
                    onPress={() => void vm.handleSaveSupervisorPin()}
                    disabled={vm.supervisorPinSaving || !vm.supervisorPinInput.trim()}
                  />
                  {vm.supervisorPinConfigured ? (
                    <SettingsSaveCta
                      label="Quitar PIN"
                      variant="secondary"
                      saving={vm.supervisorPinSaving}
                      layoutWide={layoutWide}
                      onPress={() => void vm.handleClearSupervisorPin()}
                      disabled={vm.supervisorPinSaving}
                    />
                  ) : null}
                </View>
              ) : null}
            </PaperSheet>
          </View>
        );
      }}
    </BranchSettingsHubShell>
  );
}
